/**
 * Authenticate a family's published encryption public key (PKA site A1/A2/A3).
 *
 * The family public key is served by the untrusted server, so wrapping a secret
 * to it blindly lets a malicious server substitute a key it controls and read
 * the family's shared items. The founder signs the family public key with their
 * identity key; we verify that signature against the founder's identity, which
 * is pinned TOFU (a changed identity blocks — never silently trusted).
 *
 * Enforcement is universal: a family with no signature is rejected (grandfathering
 * was retired). A malicious server cannot forge the founder's signature, and it
 * cannot strip it to force a downgrade — an unsigned key fails outright.
 */
import { verifyFamilyKeySignature } from '@heybesafe/shared';
import type { FamilyKeyClaim } from '@heybesafe/shared';
import { pinIdentity } from './pinnedIdentities';

export type { FamilyKeyClaim };

/**
 * True iff the family public key is trustworthy: it carries a valid founder
 * signature over exactly this public key (verified by the shared
 * `verifyFamilyKeySignature`), AND the founder's identity — pinned TOFU — is
 * unchanged (a changed key blocks). A missing signature is rejected.
 */
export function familyKeyAuthentic(org: FamilyKeyClaim): boolean {
  if (!verifyFamilyKeySignature(org)) return false;
  // A verified signature guarantees adminIdentityPublicKey is present.
  // TOFU: pin the founder's identity; a changed key blocks.
  return !pinIdentity(org.adminUserId, org.adminIdentityPublicKey!).changed;
}
